import React, { FC } from 'react';
import clsx from 'clsx';
import { theme } from 'theme';
import { makeStyles } from '@mui/styles';
import { LinearProgress } from '@mui/material';
import Box, { BoxProps } from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { useReportPage } from 'shared/hooks';
import Grid from '@mui/material/Grid';
import { LegendColorChip } from 'ui/ColorChips/LegendColorChip';

export enum DistributionBarVariant {
  Variant1,
  Variant2,
}

type DistributionScores = {
  scoreCompany: number;
  scoreSupplyChain: number;
};

type DistributionBarProps = {
  distributionScores?: DistributionScores;
  distributionProgress: number;
  labelA?: string;
  labelB?: string;
  labelC?: string;
  variant?: DistributionBarVariant;
  colorBg?: string;
  colorProgress?: string;
  colorDownstream?: string;
  isDouble?: boolean;
  downstreamProgress?: number;
};

type IndicatorProps = {
  width?: number;
  height?: number;
  left?: number | string;
  right?: number | string;
  color: string;
  value: number;
  showIcons?: boolean;
} & BoxProps;

type ProgressStyleProps = {
  variant: DistributionBarVariant;
  colorBg: string;
  colorProgress?: string;
  colorDownstream?: string;
};

const useStyles = makeStyles(() => ({
  label: {
    fontSize: useReportPage ? 10 : 12,
  },
  percentageLabel: {
    fontSize: useReportPage ? 12 : 16,
  },
  barWrapper: {
    position: 'relative',
  },
  scoresWrapper: {
    display: 'flex',
    alignItems: 'flex-end',
    paddingTop: 15,
  },
  score: {
    fontSize: useReportPage ? 12 : 22,
    width: '100%',
  },
  scoreLast: { textAlign: 'right' },
  flip: {
    transform: 'rotate(360deg) scaleX(-1)',
    bottom: 15,
  },
  chipSpacing: {
    marginRight: theme.spacing(1),
  },
}));

const Indicator: FC<IndicatorProps> = ({
  width = 25,
  height = 20,
  left,
  right,
  color = theme.palette.grey[500],
  value = 0,
  showIcons,
  ...boxProps
}) => {
  const classes = useStyles();

  return (
    <Box
      {...boxProps}
      left={left ? `calc(${left} - ${width / 2}px)` : undefined}
      right={right ? `calc(${right} - ${width / 2}px)` : undefined}
    >
      <Box
        position="relative"
        display="flex"
        justifyContent="center"
        width={0}
        height={0}
        style={
          showIcons
            ? {
                borderStyle: 'solid',
                borderWidth: `0 9px 15px 9px`,
                borderColor: `transparent transparent ${color} transparent`,
              }
            : undefined
        }
      >
        <Box
          position="absolute"
          top={height}
          border={showIcons ? 1 : 0}
          borderColor={color}
          borderRadius="20px"
          paddingX={2.5}
        >
          <Typography variant="body1" className={classes.percentageLabel}>{`${value}%`}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const useProgressStyles = makeStyles({
  root: ({ variant, colorBg }: ProgressStyleProps) => ({
    height: variant === DistributionBarVariant.Variant1 ? 15 : 50,
    borderRadius: variant === DistributionBarVariant.Variant1 ? 15 : 8,
    backgroundColor: colorBg,
  }),
  bar: ({ variant, colorProgress }: ProgressStyleProps) => ({
    borderRadius: variant === DistributionBarVariant.Variant1 ? 20 : 8,
    backgroundColor: colorProgress,
  }),
});

const useDownstreamStyles = makeStyles({
  root: ({ variant }: ProgressStyleProps) => ({
    height: variant === DistributionBarVariant.Variant1 ? 15 : 50,
    borderRadius: variant === DistributionBarVariant.Variant1 ? 15 : 8,
    backgroundColor: 'transparent',
  }),
  bar: ({ variant, colorDownstream }: ProgressStyleProps) => ({
    borderRadius: variant === DistributionBarVariant.Variant1 ? 20 : 8,
    backgroundColor: colorDownstream,
  }),
});

const DistributionBar: FC<DistributionBarProps> = ({
  distributionScores,
  distributionProgress,
  downstreamProgress,
  labelA,
  labelB,
  labelC,
  variant = DistributionBarVariant.Variant1,
  colorBg = '#AECCE5',
  colorProgress = '#013B6C',
  colorDownstream = '#598CB8',
  isDouble = false,
}) => {
  const classes = useStyles();

  const progressClasses = useProgressStyles({ variant, colorBg, colorProgress });
  const downstreamProgressClasses = useDownstreamStyles({ variant, colorBg, colorDownstream });

  const _downstreamProgress: number = downstreamProgress || 0;

  const supplyChainValue = (100 - distributionProgress - _downstreamProgress).toFixed(1);

  const supplyChainArrow = (100 - _downstreamProgress) / 2 + distributionProgress / 2;

  return (
    <Box position="relative">
      <Grid container>
        {distributionScores && (
          <Grid item xs className={classes.scoresWrapper}>
            <Typography variant="body1" className={classes.score}>
              {`${distributionScores.scoreCompany}/10`}
            </Typography>
          </Grid>
        )}
        <Grid item xs={distributionScores ? 9 : 12} className={classes.barWrapper}>
          <Box display="flex" justifyContent="space-between" marginBottom={1}>
            <Box display="flex" alignItems="center">
              <LegendColorChip className={classes.chipSpacing} chipColor={colorProgress} />
              <Typography variant="body2" className={classes.label}>
                <strong>{labelA}</strong>
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <LegendColorChip className={classes.chipSpacing} chipColor={colorBg} />
              <Typography variant="body2" className={classes.label}>
                <strong>{labelB}</strong>
              </Typography>
            </Box>
            {isDouble && (
              <Box display="flex" alignItems="center">
                <LegendColorChip className={classes.chipSpacing} chipColor={colorDownstream} />
                <Typography variant="body2" className={classes.label}>
                  <strong>{labelC}</strong>
                </Typography>
              </Box>
            )}
          </Box>
          <LinearProgress
            variant="determinate"
            value={distributionProgress}
            classes={progressClasses}
          />
          {isDouble && (
            <LinearProgress
              variant="determinate"
              value={_downstreamProgress}
              classes={downstreamProgressClasses}
              className={classes.flip}
            />
          )}
          <Indicator
            position="absolute"
            top={useReportPage ? 37 : 40}
            value={distributionProgress}
            left={
              variant === DistributionBarVariant.Variant1 ? `${distributionProgress / 2}%` : '50%'
            }
            color={theme.palette.grey[500]}
            showIcons={variant === DistributionBarVariant.Variant1}
            height={variant === DistributionBarVariant.Variant1 ? 20 : 30}
          />
          {variant === DistributionBarVariant.Variant1 && (
            <Indicator
              position="absolute"
              top={useReportPage ? 37 : 40}
              value={+supplyChainValue}
              left={`${supplyChainArrow}%`}
              color={theme.palette.grey[500]}
              showIcons={variant === DistributionBarVariant.Variant1}
              height={variant === DistributionBarVariant.Variant1 ? 20 : 30}
            />
          )}
          {isDouble && (
            <Indicator
              position="absolute"
              top={useReportPage ? 37 : 40}
              value={_downstreamProgress}
              right={`${_downstreamProgress / 2}%`}
              color={theme.palette.grey[500]}
              showIcons={variant === DistributionBarVariant.Variant1}
              height={variant === DistributionBarVariant.Variant1 ? 20 : 30}
            />
          )}
        </Grid>
        {distributionScores && (
          <Grid item xs className={classes.scoresWrapper}>
            <Typography variant="body1" className={clsx(classes.score, classes.scoreLast)}>
              {`${distributionScores.scoreSupplyChain}/10`}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default DistributionBar;
